import React from "react"
import PropTypes from "prop-types"
import { useDispatch, useSelector } from "react-redux"
import { fetchQuestionnaire, submitQuestionnaire } from "../../../../../store/actions/QuestionnaireActions"

const QuestionnaireComponent = ({ leadId, formId, onSubmit, isConverted, onUnsavedChangesChange }) => {
  const dispatch = useDispatch()
  const { fields, answers, loading, error, submitting } = useSelector((state) => state.questionnaireState)
  const [formAnswers, setFormAnswers] = React.useState(answers || {})
  const [filePreviews, setFilePreviews] = React.useState({})
  const [uploadedFiles, setUploadedFiles] = React.useState({})

  // Add state to track form validation errors
  const [formErrors, setFormErrors] = React.useState({})

  // Add state to track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = React.useState(false)
  const [initialAnswers, setInitialAnswers] = React.useState({})
  const [visibleOptionQuestions, setVisibleOptionQuestions] = React.useState({})

  const [isSubmitted, setIsSubmitted] = React.useState(false)
  const [submissionComplete, setSubmissionComplete] = React.useState(false)

  leadId = leadId || (fields ? fields.lead_id : null)

  // Debug logging
  console.log("QuestionnaireComponent - fields:", fields)
  console.log("QuestionnaireComponent - fields length:", fields?.length)

  // Count questions by type
  if (fields && fields.length > 0) {
    const questionTypes = {}
    fields.forEach((field) => {
      if (field.isGroup === "Yes" && field.questions) {
        field.questions.forEach((q) => {
          questionTypes[q.question_type] = (questionTypes[q.question_type] || 0) + 1
        })
      } else {
        questionTypes[field.question_type] = (questionTypes[field.question_type] || 0) + 1
      }
    })
    console.log("Question types count:", questionTypes)
  }

  React.useEffect(() => {
    if (leadId) {
      console.log("Fetching questionnaire for leadId:", leadId)
      dispatch(fetchQuestionnaire(leadId))
    }
  }, [dispatch, leadId])

  React.useEffect(() => {
    if (answers) {
      setFormAnswers((prev) => ({
        ...prev,
        ...answers,
      }))
      // Set initial answers for comparison
      setInitialAnswers(answers)
    }
  }, [answers])

  React.useEffect(() => {
    if (fields && fields.length > 0) {
      const answersFromAPI = {}
      let hasExistingAnswers = false

      fields.forEach((field) => {
        if (field.isGroup === "Yes" && field.questions) {
          // Handle grouped questions
          field.questions.forEach((question) => {
            if (question.answer && question.answer.trim() !== "") {
              if (question.question_type === "Multiple Selection" && question.answer.includes(",")) {
                answersFromAPI[question.form_question_id] = question.answer.split(",").map((item) => item.trim())
              } else if (question.question_type === "File Input") {
                // Handle file answers from API
                try {
                  // Try parsing as JSON first
                  const fileData = JSON.parse(question.answer)
                  answersFromAPI[question.form_question_id] = fileData
                  console.log("[v0] File loaded from API (JSON):", fileData.fileName)
                } catch (e) {
                  // If not JSON, check if it's a base64 data URI
                  if (question.answer.startsWith("data:")) {
                    const fileData = {
                      fileBase64: question.answer,
                      fileName: `file_${question.form_question_id}`,
                      fileType: question.answer.split(";")[0].replace("data:", ""),
                    }
                    answersFromAPI[question.form_question_id] = fileData
                    console.log("[v0] File loaded from API (base64):", fileData.fileName, fileData.fileType)
                  } else {
                    const fileUrl = question.answer;

                    const fileData = {
                      fileBase64: fileUrl,
                      fileName: fileUrl.split("/").pop(),
                      fileType: fileUrl.match(/\.(jpg|jpeg|png|gif)$/i)
                        ? "image/png"
                        : fileUrl.match(/\.pdf$/i)
                          ? "application/pdf"
                          : "application/octet-stream",
                    };

                    answersFromAPI[question.form_question_id] = fileData;

                    console.log("[v0] File loaded from API (URL):", fileData);

                  }
                }
              } else {
                answersFromAPI[question.form_question_id] = question.answer
              }
              hasExistingAnswers = true
            }
          })
        } else if (field.answer && field.answer.trim() !== "") {
          // Handle individual questions
          if (field.question_type === "Multiple Selection" && field.answer.includes(",")) {
            answersFromAPI[field.form_question_id] = field.answer.split(",").map((item) => item.trim())
          } else if (field.question_type === "File Input") {
            // Handle file answers from API
            try {
              // Try parsing as JSON first
              const fileData = JSON.parse(field.answer)
              answersFromAPI[field.form_question_id] = fileData
              console.log("[v0] File loaded from API (JSON):", fileData.fileName)
            } catch (e) {
              // If not JSON, check if it's a base64 data URI
              if (field.answer.startsWith("data:")) {
                const fileData = {
                  fileBase64: field.answer,
                  fileName: `file_${field.form_question_id}`,
                  fileType: field.answer.split(";")[0].replace("data:", ""),
                }
                answersFromAPI[field.form_question_id] = fileData
                console.log("[v0] File loaded from API (base64):", fileData.fileName, fileData.fileType)
              } else {
                const fileUrl = field.answer;
              
                const fileData = {
                  fileBase64: fileUrl,
                  fileName: fileUrl.split("/").pop(),
                  fileType: fileUrl.match(/\.(jpg|jpeg|png|gif)$/i)
                    ? "image/png"
                    : fileUrl.match(/\.pdf$/i)
                    ? "application/pdf"
                    : "application/octet-stream",
                };
              
                answersFromAPI[field.form_question_id] = fileData;
              
                console.log("[v0] File loaded from API (URL):", fileData);
              }
            }
          } else {
            answersFromAPI[field.form_question_id] = field.answer
          }
          hasExistingAnswers = true
        }
      })

      console.log("Answers from API:", answersFromAPI)

      if (Object.keys(answersFromAPI).length > 0) {
        setFormAnswers((prev) => ({
          ...prev,
          ...answersFromAPI,
        }))
        setInitialAnswers(answersFromAPI)

        if (hasExistingAnswers) {
          setIsSubmitted(true)
          setSubmissionComplete(true)
        }
      }
    }
  }, [fields])

  React.useEffect(() => {
    // If already submitted or converted, no unsaved changes
    if (submissionComplete || isConverted) {
      setHasUnsavedChanges(false)
      if (onUnsavedChangesChange) {
        onUnsavedChangesChange(false)
      }
      return
    }

    // Check if user has made any changes
    const hasAnyAnswers = Object.keys(formAnswers).some((key) => {
      const value = formAnswers[key]
      return value && value !== "" && (!Array.isArray(value) || value.length > 0)
    })

    // Check if answers differ from initial state
    const hasChanges = JSON.stringify(formAnswers) !== JSON.stringify(initialAnswers)

    const shouldShowUnsaved = hasAnyAnswers && hasChanges && !submissionComplete

    console.log("Unsaved changes check:", {
      hasAnyAnswers,
      hasChanges,
      submissionComplete,
      shouldShowUnsaved,
    })

    setHasUnsavedChanges(shouldShowUnsaved)

    // Notify parent component about unsaved changes
    if (onUnsavedChangesChange) {
      onUnsavedChangesChange(shouldShowUnsaved)
    }
  }, [formAnswers, initialAnswers, submissionComplete, isConverted, onUnsavedChangesChange])

  // Validation function for form fields
  const validateField = (questionId, value, questionType, isRequired) => {
    if (!isRequired || isConverted) return null

    if (questionType === "Multiple Selection" || questionType === "Checkbox") {
      // For multiple selection, at least one option should be selected
      if (!value || (Array.isArray(value) && value.length === 0)) {
        return "Please select at least one option"
      }
    } else if (questionType === "Multiple Choice" || questionType === "Single Selection") {
      // For single selection, one option must be selected
      if (!value || value.trim() === "") {
        return "Please select an option"
      }
    } else if (questionType === "Short Answer") {
      // For text fields, check if not empty
      if (!value || value.trim() === "") {
        return "This field is required"
      }
    } else if (questionType === "Paragraph") {
      // For paragraph fields
      if (!value || value.trim() === "") {
        return "This field is required"
      }
    } else if (questionType === "Date" || questionType === "Time") {
      // Ensure Date/Time required fields are not empty
      if (!value || String(value).trim() === "") {
        return "This field is required"
      }
    } else if (questionType === "File Input") {
      // For file inputs - don't validate if there's existing file from API
      if (!value) {
        return "Please upload a file"
      }
    }

    return null
  }

  // Validate all fields
  const validateForm = () => {
    const errors = {}

    if (!fields) return errors

    fields.forEach((field) => {
      if (field.isGroup === "Yes" && field.questions) {
        // Validate questions in groups
        field.questions.forEach((question) => {
          const error = validateField(
            question.form_question_id,
            formAnswers[question.form_question_id],
            question.question_type,
            question.is_required === "Yes",
          )
          if (error) {
            errors[question.form_question_id] = error
          }

          // Validate option questions if they are visible
          if (question.options && question.options.length > 0) {
            question.options.forEach((option) => {
              if (option.option_questions && option.option_questions.length > 0) {
                const optionKey = `${question.form_question_id}_${option.option_id}`
                const isVisible = visibleOptionQuestions[optionKey]

                if (isVisible) {
                  option.option_questions.forEach((optionQuestion) => {
                    const optionError = validateField(
                      optionQuestion.form_question_id,
                      formAnswers[optionQuestion.form_question_id],
                      optionQuestion.type || "Short Answer",
                      optionQuestion.isRequired,
                    )
                    if (optionError) {
                      errors[optionQuestion.form_question_id] = optionError
                    }
                  })
                }
              }
            })
          }
        })
      } else {
        // Validate individual questions
        const error = validateField(
          field.form_question_id,
          formAnswers[field.form_question_id],
          field.question_type,
          field.is_required === "Yes",
        )
        if (error) {
          errors[field.form_question_id] = error
        }

        // Validate option questions for individual questions if they are visible
        if (field.options && field.options.length > 0) {
          field.options.forEach((option) => {
            if (option.option_questions && option.option_questions.length > 0) {
              const optionKey = `${field.form_question_id}_${option.option_id}`
              const isVisible = visibleOptionQuestions[optionKey]

              if (isVisible) {
                option.option_questions.forEach((optionQuestion) => {
                  const optionError = validateField(
                    optionQuestion.form_question_id,
                    formAnswers[optionQuestion.form_question_id],
                    optionQuestion.type || "Short Answer",
                    optionQuestion.isRequired,
                  )
                  if (optionError) {
                    errors[optionQuestion.form_question_id] = optionError
                  }
                })
              }
            }
          })
        }
      }
    })

    return errors
  }

  // Handle file upload and preview
  const handleFileUpload = async (questionId, file) => {
    if (isConverted) return

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
    const maxSize = 5 * 1024 * 1024 // 5MB

    if (!allowedTypes.includes(file.type)) {
      setFormErrors(prev => ({
        ...prev,
        [questionId]: "Please upload only PNG, JPEG, PDF, or DOC files"
      }))
      return
    }

    if (file.size > maxSize) {
      setFormErrors(prev => ({
        ...prev,
        [questionId]: "File size should be less than 5MB"
      }))
      return
    }

    // Clear error for this field
    if (formErrors[questionId]) {
      setFormErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[questionId]
        return newErrors
      })
    }

    // Create preview URL
    const previewUrl = URL.createObjectURL(file)

    // Store file and preview
    setFilePreviews(prev => ({
      ...prev,
      [questionId]: previewUrl
    }))

    setUploadedFiles(prev => ({
      ...prev,
      [questionId]: file
    }))

    // Store file info in formAnswers
    setFormAnswers(prev => ({
      ...prev,
      [questionId]: {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        file: file,
        preview: previewUrl
      }
    }))
  }

  // Remove uploaded file
  const removeFile = (questionId) => {
    if (isConverted) return

    // Revoke object URL to avoid memory leaks
    if (filePreviews[questionId]) {
      URL.revokeObjectURL(filePreviews[questionId])
    }

    setFilePreviews(prev => {
      const newPreviews = { ...prev }
      delete newPreviews[questionId]
      return newPreviews
    })

    setUploadedFiles(prev => {
      const newFiles = { ...prev }
      delete newFiles[questionId]
      return newFiles
    })

    setFormAnswers(prev => {
      const newAnswers = { ...prev }
      delete newAnswers[questionId]
      return newAnswers
    })
  }

  // Get file icon based on file type
  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) return 'fa fa-image'
    if (fileType === 'application/pdf') return 'fa fa-file-pdf-o'
    if (fileType?.includes('word')) return 'fa fa-file-word-o'
    return 'fa fa-file-o'
  }

  // Render file preview
  const renderFilePreview = (questionId, fileData) => {
    const preview = filePreviews[questionId] || fileData?.preview
    const fileType = fileData?.fileType || uploadedFiles[questionId]?.type
    const fileName = fileData?.fileName || uploadedFiles[questionId]?.name

    if (!preview && !fileName) return null

    return (
      <div className="mt-2">
        {fileType?.startsWith('image/') && (preview || fileData?.fileBase64) ? (
          <div className="position-relative d-inline-block">
            <img
              src={preview || fileData?.fileBase64}
              alt="Preview"
              style={{
                maxWidth: '200px',
                maxHeight: '200px',
                objectFit: 'cover',
                borderRadius: '8px',
                border: '1px solid #ddd'
              }}
            />
            {!isConverted && (
              <button
                type="button"
                className="btn btn-sm btn-danger position-absolute top-0 end-0"
                style={{ borderRadius: '50%', padding: '2px 6px' }}
                onClick={() => removeFile(questionId)}
              >
                ×
              </button>
            )}
          </div>
        ) : (
          <div className="d-flex align-items-center justify-content-between p-2 border rounded" style={{ maxWidth: '300px', backgroundColor: '#fff' }}>
            <div>
              <i className={`${getFileIcon(fileType)} fa-2x me-2`} aria-hidden="true"></i>
              <span className="small">{fileName || 'Uploaded file'}</span>
            </div>
            {!isConverted && (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => removeFile(questionId)}
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  const renderQuestionField = (question) => {
    const { form_question_id, label, question_type, is_required, options = [] } = question

    console.log("Rendering question:", {
      form_question_id,
      label,
      question_type,
      options,
    })

    const required = is_required === "Yes" && !isConverted
    const currentFileData = formAnswers[form_question_id]

    const renderOptionQuestionInput = (optionQuestion) => {
      const currentValue = formAnswers[optionQuestion.form_question_id] || ""
      const questionType = optionQuestion.type || "Short Answer"

      switch (questionType) {
        case "Short Answer":
          return (
            <div>
              <input
                className="form-control mt-2"
                type="text"
                value={currentValue}
                onChange={(e) =>
                  handleInputChange({
                    target: {
                      name: optionQuestion.form_question_id,
                      value: e.target.value,
                      type: "text",
                    },
                  })
                }
                placeholder="Enter your answer"
                disabled={isConverted}
              />
              {formErrors[optionQuestion.form_question_id] && (
                <div className="text-danger small mt-1">{formErrors[optionQuestion.form_question_id]}</div>
              )}
            </div>
          )

        case "Paragraph":
          return (
            <div>
              <textarea
                className="form-control mt-2"
                value={currentValue}
                onChange={(e) =>
                  handleInputChange({
                    target: {
                      name: optionQuestion.form_question_id,
                      value: e.target.value,
                      type: "text",
                    },
                  })
                }
                rows={3}
                placeholder="Enter your detailed answer"
                disabled={isConverted}
              />
              {formErrors[optionQuestion.form_question_id] && (
                <div className="text-danger small mt-1">{formErrors[optionQuestion.form_question_id]}</div>
              )}
            </div>
          )

        case "Multiple Choice":
        case "Single Selection":
          return (
            <div className="mt-2">
              {(optionQuestion.options || []).map((opt, optIndex) => (
                <div className="form-check" key={opt.form_question_id || optIndex}>
                  <input
                    type="radio"
                    className="form-check-input"
                    name={optionQuestion.form_question_id}
                    value={opt.label}
                    checked={currentValue === opt.label}
                    onChange={(e) =>
                      handleInputChange({
                        target: {
                          name: optionQuestion.form_question_id,
                          value: opt.label,
                          type: "radio",
                        },
                      })
                    }
                    disabled={isConverted}
                  />
                  <label className="form-check-label">{opt.label}</label>
                </div>
              ))}
              {formErrors[optionQuestion.form_question_id] && (
                <div className="text-danger small mt-1">{formErrors[optionQuestion.form_question_id]}</div>
              )}
            </div>
          )

        case "Multiple Selection":
        case "Checkbox":
          return (
            <div className="mt-2">
              {(optionQuestion.options || []).map((opt, optIndex) => {
                const currentValues = Array.isArray(currentValue) ? currentValue : currentValue ? [currentValue] : []

                return (
                  <div className="form-check" key={opt.form_question_id || optIndex}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name={optionQuestion.form_question_id}
                      value={opt.label}
                      checked={currentValues.includes(opt.label)}
                      onChange={(e) =>
                        handleInputChange({
                          target: {
                            name: optionQuestion.form_question_id,
                            value: opt.label,
                            type: "checkbox",
                            checked: e.target.checked,
                          },
                        })
                      }
                      disabled={isConverted}
                    />
                    <label className="form-check-label">{opt.label}</label>
                  </div>
                )
              })}
              {formErrors[optionQuestion.form_question_id] && (
                <div className="text-danger small mt-1">{formErrors[optionQuestion.form_question_id]}</div>
              )}
            </div>
          )

        case "Date":
        case "Time":
          return (
            <div>
              <input
                className="form-control mt-2"
                type={questionType.toLowerCase()}
                value={currentValue}
                onChange={(e) =>
                  handleInputChange({
                    target: {
                      name: optionQuestion.form_question_id,
                      value: e.target.value,
                      type: questionType.toLowerCase(),
                    },
                  })
                }
                placeholder={`Enter your ${questionType}`}
                disabled={isConverted}
              />
              {formErrors[optionQuestion.form_question_id] && (
                <div className="text-danger small mt-1">{formErrors[optionQuestion.form_question_id]}</div>
              )}
            </div>
          )

        default:
          return (
            <div>
              <input
                className="form-control mt-2"
                type="text"
                value={currentValue}
                onChange={(e) =>
                  handleInputChange({
                    target: {
                      name: optionQuestion.form_question_id,
                      value: e.target.value,
                      type: "text",
                    },
                  })
                }
                placeholder="Enter your answer"
                disabled={isConverted}
              />
              {formErrors[optionQuestion.form_question_id] && (
                <div className="text-danger small mt-1">{formErrors[optionQuestion.form_question_id]}</div>
              )}
            </div>
          )
      }
    }

    switch (question_type) {
      case "Multiple Choice":
      case "Single Selection":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <div>
              {options?.map((option) => {
                const hasOptionQuestions = option.option_questions && option.option_questions.length > 0
                return (
                  <div className="form-check" key={option.option_id}>
                    <input
                      type="radio"
                      className="form-check-input"
                      name={form_question_id}
                      value={option.option_name}
                      checked={formAnswers[form_question_id] === option.option_name}
                      onChange={handleInputChange}
                      disabled={isConverted}
                    />
                    <label className="form-check-label">
                      {option.option_name}
                      {hasOptionQuestions && (
                        <span className="badge bg-info ms-2" title={`${option.option_questions.length} sub-questions`}>
                          {option.option_questions.length}
                        </span>
                      )}
                    </label>
                    {/* Render option questions directly below the selected option */}
                    {formAnswers[form_question_id] === option.option_name && hasOptionQuestions && (
                      <div className="ms-4 mt-2 mb-3 p-3 bg-light rounded" style={{ borderLeft: "3px solid #007bff" }}>
                        {option.option_questions.map((optionQuestion, optionIdx) => (
                          <div key={optionQuestion.form_question_id} className="mb-3">
                            <label className="form-label">
                              <strong>{optionQuestion.label}</strong>
                              {optionQuestion.isRequired && <span className="text-danger"> *</span>}
                            </label>
                            {formErrors[optionQuestion.form_question_id] && (
                              <div className="text-danger small">{formErrors[optionQuestion.form_question_id]}</div>
                            )}
                            {renderOptionQuestionInput(optionQuestion)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      case "Multiple Selection":
      case "Checkbox":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <div>
              {options?.map((option) => {
                const hasOptionQuestions = option.option_questions && option.option_questions.length > 0
                const isSelected = Array.isArray(formAnswers[form_question_id])
                  ? formAnswers[form_question_id].includes(option.option_name)
                  : formAnswers[form_question_id] === option.option_name

                return (
                  <div className="form-check" key={option.option_id}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      name={form_question_id}
                      value={option.option_name}
                      checked={isSelected}
                      onChange={handleInputChange}
                      disabled={isConverted}
                    />
                    <label className="form-check-label">
                      {option.option_name}
                      {hasOptionQuestions && (
                        <span className="badge bg-info ms-2" title={`${option.option_questions.length} sub-questions`}>
                          {option.option_questions.length}
                        </span>
                      )}
                    </label>
                    {/* Render option questions directly below the selected option */}
                    {isSelected && hasOptionQuestions && (
                      <div className="ms-4 mt-2 mb-3 p-3 bg-light rounded" style={{ borderLeft: "3px solid #007bff" }}>
                        {option.option_questions.map((optionQuestion, optionIdx) => (
                          <div key={optionQuestion.form_question_id} className="mb-3">
                            <label className="form-label">
                              <strong>{optionQuestion.label}</strong>
                              {optionQuestion.isRequired && <span className="text-danger"> *</span>}
                            </label>
                            {formErrors[optionQuestion.form_question_id] && (
                              <div className="text-danger small">{formErrors[optionQuestion.form_question_id]}</div>
                            )}
                            {renderOptionQuestionInput(optionQuestion)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      case "Short Answer":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <textarea
              className="form-control"
              rows="3"
              placeholder={`Enter your answer`}
              name={form_question_id}
              value={formAnswers[form_question_id] || ""}
              onChange={handleInputChange}
              disabled={isConverted}
            />
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      case "Paragraph":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <textarea
              className="form-control"
              rows="5"
              placeholder="Enter your detailed answer"
              name={form_question_id}
              value={formAnswers[form_question_id] || ""}
              onChange={handleInputChange}
              disabled={isConverted}
            />
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      case "File Input":
        // Check if file exists from API or newly uploaded
        const hasFileData = currentFileData && (currentFileData.fileBase64 || currentFileData.fileName || currentFileData.file)

        console.log(`[v0] File Input [${form_question_id}]:`, {
          hasFileData,
          currentFileData,
          fileName: currentFileData?.fileName,
          fileType: currentFileData?.fileType,
          hasBase64: !!currentFileData?.fileBase64,
        })

        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa' }}>
              {/* File upload input - hide if file already exists and converted */}
              {(!isConverted || !hasFileData) && (
                <div className="mb-3">
                  <input
                    type="file"
                    className="form-control"
                    accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files[0]
                      if (file) {
                        handleFileUpload(form_question_id, file)
                      }
                    }}
                    disabled={isConverted}
                  />
                  <small className="text-muted">
                    Allowed files: PNG, JPEG, PDF, DOC (Max 5MB)
                  </small>
                </div>
              )}

              {/* Show existing file from API or newly uploaded file */}
              {hasFileData && (
                <div className="mt-2">
                  {/* 🖼 IMAGE */}
                  {currentFileData.fileType?.startsWith("image/") && (
                    <div className="position-relative d-inline-block">
                      <img
                        src={currentFileData.fileBase64 || filePreviews[form_question_id]}
                        alt={currentFileData.fileName}
                        style={{
                          maxWidth: "200px",
                          maxHeight: "200px",
                          borderRadius: "8px",
                          border: "1px solid #ddd",
                          cursor: "pointer"
                        }}
                        onClick={() => window.open(currentFileData.fileBase64)}
                      />

                      {!isConverted && (
                        <span
                          onClick={() => removeFile(form_question_id)}
                          style={{
                            position: "absolute",
                            top: "-6px",
                            right: "-6px",
                            background: "red",
                            color: "white",
                            borderRadius: "50%",
                            width: "18px",
                            height: "18px",
                            fontSize: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            zIndex: 10
                          }}
                        >
                          ×
                        </span>
                      )}
                    </div>
                  )}

                  {/* 📄 PDF */}
                  {currentFileData.fileType === "application/pdf" && (
                    <div>
                      <iframe
                        src={currentFileData.fileBase64}
                        width="100%"
                        height="300px"
                        style={{ border: "1px solid #ddd", borderRadius: "8px" }}
                      />
                      <a
                        href={currentFileData.fileBase64}
                        download={currentFileData.fileName}
                        className="btn btn-sm btn-primary mt-2"
                      >
                        Download PDF
                      </a>
                    </div>
                  )}

                  {/* 📁 OTHER FILES (DOC, DOCX, etc.) */}
                  {!currentFileData.fileType?.startsWith("image/") &&
                    currentFileData.fileType !== "application/pdf" && (
                      <div className="d-flex align-items-center justify-content-between p-2 border rounded">
                        <div>
                          <i className={`${getFileIcon(currentFileData.fileType)} me-2`}></i>
                          <span>{currentFileData.fileName}</span>
                        </div>
                        {currentFileData.fileBase64 && (
                          <a
                            href={currentFileData.fileBase64}
                            download={currentFileData.fileName}
                            className="btn btn-sm btn-outline-primary"
                          >
                            Download
                          </a>
                        )}
                      </div>
                    )}
                </div>
              )}

              {formErrors[form_question_id] && (
                <div className="text-danger small mt-2">{formErrors[form_question_id]}</div>
              )}
            </div>
          </div>
        )

      case "Date":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <input
              className="form-control"
              type="date"
              name={form_question_id}
              value={formAnswers[form_question_id] || ""}
              onChange={handleInputChange}
              disabled={isConverted}
            />
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      case "Time":
        return (
          <div className="col-xl-6 mb-3" key={form_question_id}>
            <label className="form-label">
              {label} {required && <span style={{ color: "red" }}>*</span>}
            </label>
            <input
              className="form-control"
              type="time"
              name={form_question_id}
              value={formAnswers[form_question_id] || ""}
              onChange={handleInputChange}
              disabled={isConverted}
            />
            {formErrors[form_question_id] && (
              <div className="text-danger small mt-1">{formErrors[form_question_id]}</div>
            )}
          </div>
        )

      default:
        console.log("Unknown question type:", question_type, "for question:", question)
        return null
    }
  }

  const renderQuestionGroup = (group) => {
    console.log("Rendering question group:", group)
    return (
      <div key={group.group_id} className="card mb-4">
        <div className="card-header">
          <h4 className="card-title">{group.label || `Group ${group.group_id}`}</h4>
        </div>
        <div className="card-body">
          <div className="row">{group.questions?.map((question) => renderQuestionField(question))}</div>
        </div>
      </div>
    )
  }

  const handleInputChange = (e) => {
    if (isConverted) return
    const { name, value, type, checked } = e.target

    console.log("Input change event:", { name, value, type, checked })

    // Clear error for this field when user starts typing/selecting
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }

    setFormAnswers((prev) => {
      let newAnswers

      if (type === "checkbox") {
        // For checkboxes, handle multiple selections
        const currentValues = Array.isArray(prev[name]) ? prev[name] : prev[name] ? [prev[name]] : []

        let newValues
        if (checked) {
          // Add the value if it's not already selected
          newValues = currentValues.includes(value) ? currentValues : [...currentValues, value]
        } else {
          // Remove the value if it's selected
          newValues = currentValues.filter((v) => v !== value)
        }

        newAnswers = {
          ...prev,
          [name]: newValues,
        }
      } else if (type === "radio") {
        // For radio buttons, always set the selected value
        newAnswers = {
          ...prev,
          [name]: value,
        }
      } else {
        // For text inputs, set the value
        newAnswers = {
          ...prev,
          [name]: value,
        }
      }

      console.log("Updated formAnswers:", newAnswers)

      return newAnswers
    })
  }

  const handleSubmit = async (e) => {
    console.log("handleSubmit called")
    e.preventDefault()
    if (isConverted) return

    console.log("Form submission started")
    console.log("Current formAnswers:", formAnswers)
    console.log("Current fields:", fields)
    setHasUnsavedChanges(false)
    if (onUnsavedChangesChange) {
      onUnsavedChangesChange(false)
    }

    // Validate form before submission
    const errors = validateForm()
    if (Object.keys(errors).length > 0) {
      console.log("Validation errors found:", errors)
      setFormErrors(errors)
      return
    }

    // Clear any existing errors
    setFormErrors({})

    try {
      const answersArray = []
      let sequenceOrder = 1

      const form_id = fields?.form_id || fields?.[0]?.form_id || formId

      console.log("Extracting form_id from API response:", form_id)
      console.log("Fields data:", fields)

      // Helper function to find question details
      const findQuestionDetails = (questionId) => {
        // Check top-level questions (not in a group)
        for (const field of fields) {
          if (field.isGroup === "No" && field.form_question_id === questionId) {
            return {
              question_id: field.question_id || field.form_question_id,
              question: field.label,
              group_id: field.group_id ?? 0,
              question_type: field.question_type,
            }
          }
        }

        // Check questions inside groups
        for (const field of fields) {
          if (field.isGroup === "Yes" && field.questions) {
            for (const question of field.questions) {
              if (question.form_question_id === questionId) {
                return {
                  question_id: question.question_id || question.form_question_id,
                  question: question.label,
                  group_id: field.group_id ?? 0,
                  question_type: question.question_type,
                }
              }
            }
          }
        }
        return null
      }

      // Helper function to find option_id for selected answer
      const findOptionId = (questionId, answerValue) => {
        // Check top-level questions first
        for (const field of fields) {
          if (!field.isGroup && field.form_question_id === questionId && field.options) {
            const option = field.options.find((opt) => opt.option_name === answerValue)
            return option ? option.option_id : null
          }
        }

        // Check questions in groups
        for (const field of fields) {
          if (field.isGroup === "Yes" && field.questions) {
            for (const question of field.questions) {
              if (question.form_question_id === questionId && question.options) {
                const option = question.options.find((opt) => opt.option_name === answerValue)
                return option ? option.option_id : null
              }
            }
          }
        }
        return null
      }

      // Process each answer in formAnswers - EXACTLY LIKE PATIENTFORM
      for (const [questionId, answer] of Object.entries(formAnswers)) {
        // Filter out invalid entries
        const isValid = answer &&
          questionId !== "undefined" &&
          questionId !== "null" &&
          questionId !== "data" &&
          ((Array.isArray(answer) && answer.length > 0) ||
            (typeof answer === "string" && answer.trim() !== "") ||
            (typeof answer === "object" && (answer.file || answer.fileBase64)))

        if (!isValid) continue

        const questionDetails = findQuestionDetails(questionId)
        if (!questionDetails) continue

        console.log(`Processing question ${questionId}:`, questionDetails)

        // Handle File Input - EXACTLY LIKE PATIENTFORM
        if (questionDetails.question_type === "File Input") {
          let fileBase64 = "";
          let fileName = "";
          let fileSize = "";
          let fileType = "";

          // Case 1: New file uploaded
          if (answer?.file) {
            const reader = new FileReader();
            fileBase64 = await new Promise((resolve, reject) => {
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(answer.file);
            });
            fileName = answer.file.name;
            fileSize = answer.file.size;
            fileType = answer.file.type;
          }
          // Case 2: Existing file from API
          else if (answer?.fileBase64) {
            fileBase64 = answer.fileBase64;
            fileName = answer.fileName || `file_${questionId}`;
            fileSize = answer.fileSize || 0;
            fileType = answer.fileType || "application/octet-stream";
          }

          // Skip if no file data
          if (!fileBase64) continue;

          // EXACTLY LIKE PATIENTFORM - Store Base64 directly in answer
          const answerObj = {
            question_id: questionDetails.question_id,
            form_question_id: questionId,
            answer: fileBase64, // Direct Base64 string - NO JSON.stringify, NO "fileBase64" key
            question: questionDetails.question,
            option_id: null,
            form_id: form_id,
            sequence_order: sequenceOrder++,
            group_id: questionDetails.group_id,
          };

          // Add file metadata as separate fields (EXACTLY LIKE PATIENTFORM)
          if (fileName) answerObj.file_name = fileName;
          if (fileSize) answerObj.file_size = fileSize;
          if (fileType) answerObj.file_type = fileType;

          console.log("FILE ANSWER (like PatientForm) =>", answerObj);
          answersArray.push(answerObj);
        }
        // Handle array answers (multiple selections)
        else if (Array.isArray(answer)) {
          const optionIds = []
          answer.forEach((singleAnswer) => {
            const optionId = findOptionId(questionId, singleAnswer)
            if (optionId) {
              optionIds.push(optionId)
            }
          })

          const answerObj = {
            question_id: questionDetails.question_id,
            form_question_id: questionId,
            answer: answer.join(", "),
            question: questionDetails.question,
            option_id: optionIds.join("|"),
            form_id: form_id,
            sequence_order: sequenceOrder++,
            group_id: questionDetails.group_id,
          }

          answersArray.push(answerObj)
        }
        else {
          // Handle single answers
          const optionId = findOptionId(questionId, answer)
          const answerObj = {
            question_id: questionDetails.question_id,
            form_question_id: questionId,
            answer: answer,
            question: questionDetails.question,
            option_id: optionId,
            form_id: form_id,
            sequence_order: sequenceOrder++,
            group_id: questionDetails.group_id,
          }

          answersArray.push(answerObj)
        }
      }

      console.log("Final answersArray:", answersArray)
      console.log("Submitting to API with leadId:", leadId)

      const result = await dispatch(submitQuestionnaire(leadId, answersArray))
      console.log("API submission result:", result)

      if (result.status === "success") {
        setInitialAnswers({ ...formAnswers })
        setIsSubmitted(true)
        setSubmissionComplete(true)
        setHasUnsavedChanges(false)

        // Clean up file previews to avoid memory leaks
        Object.values(filePreviews).forEach(preview => {
          if (preview) URL.revokeObjectURL(preview)
        })

        // Refresh questionnaire data to show updated answers
        dispatch(fetchQuestionnaire(leadId))

        if (onUnsavedChangesChange) onUnsavedChangesChange(false)

        // Call parent submit handler
        onSubmit(formAnswers)

        console.log("Questionnaire submitted successfully - no more unsaved changes")
      }
    } catch (error) {
      console.error("Failed to submit form:", error)
    }
  }

  // Clean up file previews on component unmount
  React.useEffect(() => {
    return () => {
      Object.values(filePreviews).forEach(preview => {
        if (preview) URL.revokeObjectURL(preview)
      })
    }
  }, [filePreviews])

  if (loading) {
    return (
      <div className="card shadow-none">
        <div className="card-body text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="card shadow-none">
        <div className="card-body text-center text-danger">
          <p>Error loading questionnaire: {error}</p>
          <button
            className="btn btn-primary"
            onClick={() => dispatch(fetchQuestionnaire(leadId))}
            disabled={isConverted}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="card shadow-none">
        <div className="card-body">
          {isConverted && (
            <div className="alert alert-info mb-3">
              This lead has been converted. The questionnaire is in view-only mode.
            </div>
          )}
          {/* {submissionComplete && !isConverted && (
            <div className="alert alert-success mb-3">Questionnaire submitted successfully!</div>
          )} */}
          <div style={{ maxHeight: "80vh", overflowY: "auto", paddingRight: "15px" }}>
            {!fields || fields.length === 0 ? (
              <div className="alert alert-info text-center">No questionnaire is available for this lead.</div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="row">
                  {fields?.map((field, index) => {
                    console.log(`Processing field ${index}:`, field)
                    console.log(`Field ${index} isGroup:`, field.isGroup, `Type:`, typeof field.isGroup)
                    console.log(`Field ${index} isGroup === "Yes":`, field.isGroup === "Yes")

                    let renderedField
                    if (field.isGroup === "Yes") {
                      console.log(`Field ${index} is a group, rendering group`)
                      renderedField = renderQuestionGroup(field)
                    } else {
                      console.log(`Field ${index} is an individual question, rendering field`)
                      renderedField = renderQuestionField(field)
                    }

                    console.log(`Field ${index} rendered result:`, renderedField)
                    return renderedField
                  })}
                </div>
                {!isConverted && (
                  <div className="text-center mt-3">
                    <button type="submit" className="btn btn-primary" disabled={submitting}>
                      {submitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          ></span>
                          Submitting...
                        </>
                      ) : (
                        "Submit Questionnaire"
                      )}
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

QuestionnaireComponent.propTypes = {
  leadId: PropTypes.string.isRequired,
  formId: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isConverted: PropTypes.bool,
  onUnsavedChangesChange: PropTypes.func,
}

QuestionnaireComponent.defaultProps = {
  isConverted: false,
}

export default QuestionnaireComponent
